import { rankMembersAcrossBankFields } from '../member-name-match.ts'
import type { BankImportMember, BankPreviewRow, ParsedBankRow } from './types.ts'

export const BANK_AUTO_MATCH_THRESHOLD = 0.65
export const BANK_HIGH_CONFIDENCE_THRESHOLD = 0.85

function bucketFor(row: Pick<BankPreviewRow, 'amountCents' | 'removed' | 'duplicateReasons' | 'matchedUserId' | 'confidence'>) {
  if (row.removed || row.amountCents <= 0) return 'skipped' as const
  if (row.duplicateReasons.length > 0) return 'duplicate' as const
  if (row.matchedUserId == null) return 'unmatched' as const
  return row.confidence >= BANK_HIGH_CONFIDENCE_THRESHOLD ? 'high' as const : 'low' as const
}

export function matchBankRow(
  source: ParsedBankRow,
  members: readonly BankImportMember[],
): BankPreviewRow {
  const candidates = rankMembersAcrossBankFields(
    [
      source.otherParty,
      source.reference,
      source.particulars,
      source.analysisCode,
    ],
    members,
    BANK_AUTO_MATCH_THRESHOLD,
  )
  const eligible = candidates.filter((candidate) => candidate.matched)
  const best = eligible[0]
  const runnerUp = eligible[1]
  const ambiguous = !!best && !!runnerUp && best.confidence - runnerUp.confidence <= 0.05
  const matched = best && !ambiguous ? best : null
  const removed = source.amountCents <= 0
  const row: BankPreviewRow = {
    ...source,
    matchedUserId: matched?.member.id ?? null,
    matchedUserName: matched?.member.displayName ?? null,
    confidence: best?.confidence ?? candidates[0]?.confidence ?? 0,
    matchStatus: matched ? 'matched' : ambiguous ? 'ambiguous' : 'unmatched',
    manuallyMatched: false,
    duplicateReasons: [],
    removed,
    skipReason: source.amountCents < 0 ? 'not-incoming' : source.amountCents === 0 ? 'zero' : null,
    bucket: 'unmatched',
    resultStatus: removed ? 'skipped' : 'preview',
    resultMessage: null,
  }
  return { ...row, bucket: bucketFor(row) }
}

export function matchBankRows(
  rows: readonly ParsedBankRow[],
  members: readonly BankImportMember[],
): BankPreviewRow[] {
  return rows.map((row) => matchBankRow(row, members))
}

export function refreshBankBucket(row: BankPreviewRow): BankPreviewRow {
  return {
    ...row,
    bucket: bucketFor(row),
    resultStatus: row.removed ? 'skipped' : row.resultStatus === 'skipped' ? 'preview' : row.resultStatus,
  }
}
